import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Hammer, ShieldCheck, MessageSquare } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/data";

export default function About() {
  useSeo({
    title: "About — RSR Australia",
    description: "RSR Australia is a multi-trade property maintenance team — licensed, insured and coordinated. Built by tradies, run like a service business.",
  });
  return (
    <>
      {/* Hero */}
      <section data-testid="about-hero" className="relative border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <p className="overline">About RSR Australia</p>
              <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] font-extrabold">
                Australian property <br className="hidden sm:block" /> maintenance, <span className="text-accent">done properly.</span>
              </h1>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-base text-muted-foreground">
                We're a multi-trade outfit built around one promise: fewer phone calls, better outcomes. From an annoying jammed door to a full bathroom renovation, you get one team and one quote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-border">
              <img
                src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1400&q=75"
                alt="Trade work in progress"
                className="w-full h-full object-cover duotone"
              />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="overline">Our story</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              Built by tradies, run like a service business.
            </h2>
            <div className="mt-6 space-y-5 text-base text-muted-foreground leading-relaxed">
              <p>RSR Australia started on the tools — fixing things, finishing renovations, taking on the jobs other crews wouldn't show up for. We grew because clients kept asking the same question: "Can you sort the painter too? And the electrician?"</p>
              <p>Today we run a coordinated team of licensed trades across renovations, repairs, electrical, plumbing, roofing, carpentry, painting, landscaping, cleaning, security and more. One coordinator owns your job from quote to walk-through.</p>
              <p>Every member of the crew is vetted, insured and licensed where required. We don't sub jobs out to whoever's cheap that week. The same hands that quote your job are the hands that finish it.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-28 border-t border-border bg-card/30">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 mb-12">
            <span className="rule-accent" /><span className="overline">What we stand for</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            {[
              { icon: Hammer, title: "Craft over speed", body: "Sharp prep, proper materials, honest finishes. We'd rather take an extra day than hand back a job we'd come back to fix." },
              { icon: ShieldCheck, title: "Trust over transactions", body: "Same coordinator on the phone every time. Quotes you can read. Invoices that match. Nothing snuck onto the bill." },
              { icon: MessageSquare, title: "Plain English, always", body: "No jargon, no upsell theatre. We tell you what needs doing, what doesn't, and what it'll cost — in language you can use." },
            ].map((b, i) => (
              <div key={i} data-testid={`value-${i}`} className="bg-background p-8 lg:p-10 flex flex-col gap-5">
                <b.icon className="w-6 h-6 text-accent" strokeWidth={1.5} />
                <h3 className="font-display text-2xl font-bold tracking-tight">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 sm:py-28 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { k: "14", v: "Trade specialities under one roof" },
            { k: "10+ yrs", v: "On the tools across Australia" },
            { k: "100%", v: "Licensed and fully insured" },
            { k: "24–48h", v: "Reactive response on urgent jobs" },
          ].map((s, i) => (
            <div key={i} className="border-l border-accent pl-5">
              <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">{s.k}</div>
              <div className="mt-2 text-sm text-muted-foreground">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="bg-card border border-border p-8 lg:p-14 grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-8">
              <p className="overline">Ready to start?</p>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold leading-[1.05]">
                Tell us about the job. We'll come back with a clear plan.
              </h3>
            </div>
            <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
              <Link to="/contact" data-testid="about-cta-quote" className="group inline-flex items-center justify-between gap-3 bg-accent text-white px-6 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                Get a free quote
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href={`tel:${PHONE_TEL}`} data-testid="about-cta-call" className="inline-flex items-center justify-between gap-3 border border-border hover:border-foreground/40 text-foreground px-6 py-4 font-semibold text-sm transition-colors">
                <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}</span>
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
